import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { LayoutGrid, Layers, Heart, LogOut, ShieldHalf, User } from "lucide-react";
import NotificationsBell from "./NotificationsBell";
import FoundingBadge from "./FoundingBadge";
import { PlanBadge, UpgradeButton } from "./UpgradeCTA";

const links = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutGrid },
  { to: "/feed", label: "The Pool", icon: Layers },
  { to: "/matches", label: "Matches", icon: Heart },
];

export default function Navbar() {
  const { user, logout } = useAuth();
  const loc = useLocation();
  const nav = useNavigate();

  const handleLogout = async () => {
    await logout();
    nav("/login");
  };

  return (
    <header className="sticky top-0 z-50 backdrop-blur-xl bg-zinc-950/70 border-b border-white/10">
      <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
        <Link to="/dashboard" className="flex items-center gap-2.5" data-testid="nav-logo">
          <span className="font-serif font-bold text-2xl tracking-tight">
            blinxd<span className="text-primary">.</span>
          </span>
          <PlanBadge />
        </Link>
        <nav className="flex items-center gap-1">
          {links.map(({ to, label, icon: Icon }) => {
            const active = loc.pathname.startsWith(to);
            return (
              <Link
                key={to}
                to={to}
                data-testid={`nav-${label.toLowerCase().replace(/\s/g, "-")}`}
                className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  active ? "bg-primary text-white glow-primary" : "text-muted-foreground hover:text-foreground hover:bg-white/5"
                }`}
              >
                <Icon size={16} />
                <span className="hidden sm:inline">{label}</span>
              </Link>
            );
          })}
          <div className="mx-2 h-6 w-px bg-white/10" />
          {["barryrg@gmail.com", "admin@blinxd.com"].includes((user?.email || "").toLowerCase()) && (
            <Link to="/admin" data-testid="nav-admin"
              className={`flex items-center gap-2 px-3 py-2 rounded-full text-sm font-medium transition-colors ${
                loc.pathname.startsWith("/admin") ? "bg-amber-500/15 text-amber-400 border border-amber-500/25" : "text-muted-foreground hover:text-amber-400 hover:bg-amber-500/10"
              }`}>
              <ShieldHalf size={16} />
              <span className="hidden sm:inline">Admin</span>
            </Link>
          )}
          <UpgradeButton />
          <NotificationsBell />
          {user?.founding_member && <FoundingBadge size={18} />}
          <Link to="/account" data-testid="nav-account"
            className={`flex items-center gap-2 px-3 py-2 rounded-full text-sm font-medium transition-colors max-w-[190px] ${
              loc.pathname.startsWith("/account") ? "bg-primary/15 text-primary" : "text-muted-foreground hover:text-foreground hover:bg-white/5"
            }`}>
            <User size={16} className="shrink-0" />
            <span className="hidden md:block text-xs font-mono truncate">{user?.email}</span>
          </Link>
          <button
            onClick={handleLogout}
            data-testid="nav-logout"
            className="flex items-center gap-2 px-3 py-2 rounded-full text-sm text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors"
          >
            <LogOut size={16} />
          </button>
        </nav>
      </div>
    </header>
  );
}
